// src/components/student/act/StudentACTTestTaker.js
import React, { useState, useEffect, useRef } from 'react';
import { auth, db, storage } from '../../../firebaseConfig';
import {
  collection, query, where, getDocs, getCountFromServer, orderBy, doc, setDoc, getDoc, deleteDoc, serverTimestamp
} from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { LuLayoutGrid, LuZoomIn, LuBookmark, LuCalculator, LuArrowLeft, LuArrowRight, LuMinus, LuPlus } from "react-icons/lu";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { saveDiagnosticLiveProgress, markDiagnosticLiveProgressComplete } from '../diagnostics/diagnosticLiveProgress';
import { ACT_SECTION_ORDER, resolveACTSectionPlan } from '../../../utils/act/actStructure';
import { backdropDismiss } from '../../../utils/backdropDismiss';

/**
 * ======================== CONFIG & TEXT ========================
 */
const INITIAL_SECTION = 'English';

const DIRECTIONS = {
  English: `
    <div style="margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid #e2e8f0;">
        <strong>Time:</strong> __TIME_LABEL__ <span style="margin: 0 8px; color: #cbd5e1;">|</span> <strong>Number of Questions:</strong> __Q_COUNT__
    </div>
    <p style="margin-bottom: 8px; line-height: 1.6;">The English Test consists of several passages. In each passage, certain words or phrases are underlined and highlighted. For each underlined portion, choose the best alternative. If you believe the original wording is best, select <strong>“No Change.”</strong></p>
    <p style="margin-bottom: 8px; line-height: 1.6;">Some questions refer to a specific part of the passage rather than an underlined portion. These questions are identified by letters, numbers, or an asterisk within square brackets <strong>[ ]</strong>.</p>
    <p style="margin-bottom: 8px; line-height: 1.6;">Read each passage carefully before answering the questions that follow. Many questions require you to consider sentences before and after the underlined portion. Be sure to read far enough ahead to determine the best answer.</p>
    <p style="margin-bottom: 8px; line-height: 1.6;">Select the answer you believe is best for each question.</p>
    <p style="margin-bottom: 0; line-height: 1.6;">
      The timer will begin when you <strong>Start English Test. </strong>
    </p> 
  `,

  Math: `
    <div style="margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid #e2e8f0;">
        <strong>Time:</strong> __TIME_LABEL__ <span style="margin: 0 8px; color: #cbd5e1;">|</span> <strong>Number of Questions:</strong> __Q_COUNT__
    </div>
    <p style="margin-bottom: 8px; line-height: 1.6;">Solve each problem and select the correct answer.</p>
    <p style="margin-bottom: 8px; line-height: 1.6;">Do not spend too much time on any single problem. Answer as many questions as possible. You may return to unanswered questions if time permits.</p>
    <p style="margin-bottom: 8px; line-height: 1.6;">You are permitted to use an approved calculator during this test. </p>
    </p> A <strong>Desmos calculator</strong> is provided within the test interface and may be used as needed.</p>
    <p style="margin-bottom: 0; line-height: 1.6;">
      The timer will begin when you <strong>Start Math Test.</strong>
    </p>
    `,

  Reading: `
    <div style="margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid #e2e8f0;">
        <strong>Time:</strong> __TIME_LABEL__ <span style="margin: 0 8px; color: #cbd5e1;">|</span> <strong>Number of Questions:</strong> __Q_COUNT__
    </div>
    <p style="margin-bottom: 8px; line-height: 1.6;">This test contains several passages. Each passage is followed by a set of questions. Some passages may be grouped together within a single scrollable window.</p>
    <p style="margin-bottom: 8px; line-height: 1.6;">After reading each passage, choose the best answer to each question. You may refer back to the passages as often as necessary.</p>
    <p style="margin-bottom: 0; line-height: 1.6;">
      The timer will begin when you <strong>Start Reading Test. </strong>
    </p>
  `,

  Science: `
    <div style="margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid #e2e8f0;">
        <strong>Time:</strong> __TIME_LABEL__ <span style="margin: 0 8px; color: #cbd5e1;">|</span> <strong>Number of Questions:</strong> __Q_COUNT__
    </div>
    <p style="margin-bottom: 8px; line-height: 1.6;">The Science Test includes several passages, each followed by a set of questions. Read each passage carefully, then choose the best answer to each question.</p>
    <p style="margin-bottom: 8px; line-height: 1.6;">You may refer back to the passages as often as necessary while answering the questions.</p>
    <p style="margin-bottom: 0; line-height: 1.6;"><strong>Calculator use is not permitted on this test.</strong> </p>
    <p style="margin-bottom: 0; line-height: 1.6;">
      The timer will begin when you <strong>Start Science Test. </strong>
    </p>
  `
};

function sanitizeExtendedTimePercent(value) {
  const n = Number(value);
  return [0, 25, 50, 75, 100].includes(n) ? n : 0;
}

/** Base seconds come from the resolved section plan (classic vs enhanced ACT). */
function actLimitSeconds(plan, sectionKey, extendedTimePercent = 0) {
  const base = plan?.[sectionKey]?.seconds ?? 0;
  if (sectionKey === 'BREAK' || base <= 0) return base; // break never extended
  const pct = sanitizeExtendedTimePercent(extendedTimePercent);
  return Math.round(base * (1 + pct / 100));
}

const DESMOS_API_KEY = 'dcb31709b452b1cf9dc26972add0fda6';

/**
 * ======================== STYLES ========================
 */
const HEADER_HEIGHT = 64;
const FOOTER_HEIGHT = 64;

const styles = {
  page: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#f8fafc',
    overflow: 'hidden',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },

  // 1. HEADER REDESIGN
  header: {
    height: HEADER_HEIGHT,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
    background: '#1c3246', // Dark official ACT blue
    color: '#fff',
    borderBottom: 'none',
    zIndex: 30,
    fontSize: 14,
    fontWeight: 600
  },
  logoRow: { display: 'flex', alignItems: 'center', gap: 12 },
  logoImg: { height: 24, width: 'auto' }, 

  // NEW: Container for the buttons (View All, Bookmark, etc.)
  headerTools: {
    display: 'flex',
    alignItems: 'center',
    gap: 20,
    marginLeft: 40 
  },
  // NEW: Style for header buttons
  headerBtn: {
    background: 'transparent',
    border: 'none',
    color: '#cbd5e1', // Light gray text
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    fontWeight: 700,
    padding: '4px 8px',
    borderRadius: 4,
    transition: 'color 0.2s'
  },
  headerBtnActive: {
  color: '#1c3246', // Dark Blue Text
  background: '#ffffff', // White Background
  boxShadow: '0 0 10px rgba(255,255,255,0.3)'
},

  // NEW: Container for Right Side Stats (Answered / Time)
  headerStats: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: '0.5px'
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  // --- MODAL / OVERLAY STYLES ---
    overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.2)', zIndex: 100,
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    },
    gridModal: {
    width: '90%', maxWidth: '600px',
    background: '#fff', borderRadius: 8,
    boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
    display: 'flex', flexDirection: 'column',
    maxHeight: '80vh', overflow: 'hidden'
    },
  
  // Main Split
  splitWrap: { flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0},

  // LEFT PANE (Passage)
  leftPane: {
    flex: 1,
    borderRight: '1px solid #e2e8f0',
    background: '#fff',
    overflow: 'hidden', 
    position: 'relative' 
  },
  
  // RIGHT PANE (Questions) - Split Vertically
  rightPane: (isMath) => ({
    flex: 1,
    maxWidth: isMath ? '900px' : 'none',
    margin: isMath ? '0 auto' : '0',
    background: '#ffffff',
    display: 'flex',
    flexDirection: 'column', // Vertical Split
    height: '100%',
    overflow: 'hidden',
    minHeight: 0
  }),

  // Right Top: Question Area
  rightTop: {
    flex: 1, // Takes remaining space
    padding: '24px',
    overflowY: 'auto',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start', // Left Align Question
    minHeight: 0
  },
  
  // Right Bottom: Answer Area
  rightBottom: {
    height: '35%', // Fixed portion for answers
    minHeight: '200px',
    background: '#fff',
    padding: '24px',
    overflowY: 'auto',
    boxShadow: '0 -4px 6px -1px rgba(0,0,0,0.05)'
  },

  // Images
  qImg: (isMath) => ({
    maxWidth: isMath ? '500px' : '450px', // Math size vs Default size
    width: '100%',
    height: 'auto',
    display: 'block',
    marginBottom: 16,
    // Math centers the image, others align left
    alignSelf: isMath ? 'center' : 'flex-start' 
  }),

  // Options
  optionRow: (selected, struck) => ({
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '10px 14px', borderRadius: 8,
    border: selected ? '2px solid #2563eb' : '1px solid #e2e8f0',
    background: selected ? '#eff6ff' : '#fff',
    cursor: 'pointer', marginBottom: 8,
    opacity: struck ? 0.5 : 1,
    position: 'relative',
    transition: 'all 0.15s',
    textDecoration: struck ? 'line-through' : 'none'
  }),
  optionLabel: (selected) => ({
    width: 26, height: 26, borderRadius: 13,
    background: selected ? '#2563eb' : '#f1f5f9',
    color: selected ? '#fff' : '#475569',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: 12, flexShrink: 0
  }),
  zoomControls: {
    position: 'absolute', top: 10, right: 10, zIndex: 50,
    display: 'flex', flexDirection: 'column', gap: 4,
    background: '#fff', padding: 4, borderRadius: 8,
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0'
  },
  zoomBtn: {
    width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: '1px solid #cbd5e1', borderRadius: 4, background: '#f8fafc', cursor: 'pointer',
    color: '#334155'
  },
  strikeBtn: (active) => ({
    marginLeft: 'auto',
    padding: '2px 8px', fontSize: 10, borderRadius: 4,
    border: '1px solid #cbd5e1', 
    background: active ? '#fee2e2' : '#fff',
    color: active ? '#b91c1c' : '#64748b', 
    cursor: 'pointer',
    textDecoration: 'none' 
  }),

  // Footer
  footer: {
    height: FOOTER_HEIGHT,
    flexShrink: 0,
    borderTop: '1px solid #e2e8f0',
    background: '#e9ecef',
    display: 'flex', alignItems: 'center', justifyContent: 'flex-end', // Align right
    gap: 12,
    padding: '0 24px'
  },
  navBtn: (primary) => ({
    padding: '10px 24px', borderRadius: 8, 
    border: primary ? 'none' : '1px solid #cbd5e1',
    background: primary ? '#2563eb' : '#fff', 
    color: primary ? '#fff' : '#0f172a', 
    fontWeight: 600, cursor: 'pointer',
    fontSize: 14
  }),

  // Instruction Screen
  instructionPage: {
    padding: '60px max(5vw, 20px)',
    maxWidth: '800px',
    margin: '0 auto',
    height: '100%',
    overflowY: 'auto'
  },
  instructionTitle: { fontSize: 32, fontWeight: 800, color: '#0f172a', marginBottom: 24 },
  instructionText: { fontSize: 16, lineHeight: 1.8, color: '#334155', whiteSpace: 'pre-wrap' },
  startSectionBtn: {
    marginTop: 40,
    padding: '14px 32px',
    fontSize: 16, fontWeight: 700,
    background: '#2563eb', color: '#fff',
    border: 'none', borderRadius: 8, cursor: 'pointer'
  },
  warningBox: {
    marginTop: 24, padding: 16, borderRadius: 8,
    background: '#fef2f2', border: '1px solid #f87171', color: '#991b1b',
    fontWeight: 600, display: 'flex', gap: 12, alignItems: 'center'
  },

  // Floating Calculator
  floatCalc: {
    position: 'absolute',
    zIndex: 50,
    background: '#fff',
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    display: 'flex', flexDirection: 'column'
  },
  floatHeader: {
    padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    cursor: 'move', userSelect: 'none'
  }
};

/**
 * ======================== COMPONENTS ========================
 */

// 1. Safe Image Loader
const SafeImage = ({ path, style }) => {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if(!path) return;
    getDownloadURL(ref(storage, path)).then(setUrl).catch(e => console.log('Img err', e));
  }, [path]);
  if (!url) return <div style={{fontSize:12, color:'#94a3b8', padding:20}}>Loading Content...</div>;
  return <img src={url} alt="Content" style={style || styles.qImg} />;
};

// 2. Magnifier Component for Passages
const MagnifierPane = ({ src, zoomLevel, setZoomLevel }) => {
  const [showMag, setShowMag] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [imgSize, setImgSize] = useState({ width: 0, height: 0 });
  const imgRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!imgRef.current) return;
    const { left, top, width, height } = imgRef.current.getBoundingClientRect();
    const x = e.pageX - left - window.pageXOffset;
    const y = e.pageY - top - window.pageYOffset;
    
    if (x < 0 || y < 0 || x > width || y > height) {
        setShowMag(false); return;
    }
    
    setShowMag(true);
    setPos({ x, y });
    setImgSize({ width, height });
  };

  const [url, setUrl] = useState('');
  useEffect(() => {
    if(src) getDownloadURL(ref(storage, src)).then(setUrl).catch(e=>console.log(e));
  }, [src]);

  if (!url) return <div style={{padding:20}}>Loading Passage...</div>;

  return (
    <div style={{position: 'relative', height: '100%', overflowY: 'auto', padding: 24, boxSizing: 'border-box'}}>
      {/* Zoom Controls */}
      <div style={styles.zoomControls}>
        <button style={styles.zoomBtn} onClick={() => setZoomLevel(z => Math.min(z + 0.05, 3.0))} title="Zoom In"><LuPlus /></button>
        <button style={styles.zoomBtn} onClick={() => setZoomLevel(z => Math.max(z - 0.05, 1.0))} title="Zoom Out"><LuMinus /></button>
        <div style={{fontSize:10, textAlign:'center', fontWeight:700}}>{zoomLevel.toFixed(2)}x</div>
      </div>

      <div 
        style={{position: 'relative', display: 'inline-block', width: '100%'}}
        onMouseEnter={() => setShowMag(true)}
        onMouseLeave={() => setShowMag(false)}
        onMouseMove={handleMouseMove}
      >
        <img ref={imgRef} src={url} alt="Passage" style={{width:'100%', display:'block', cursor: 'crosshair'}} />
        
        {showMag && (
          <div style={{
            position: 'absolute', pointerEvents: 'none',
            height: 150, width: 150, borderRadius: '50%',
            border: '2px solid #cbd5e1', background: '#fff',
            backgroundImage: `url('${url}')`, backgroundRepeat: 'no-repeat',
            // Use zoomLevel state
            backgroundSize: `${imgSize.width * zoomLevel}px ${imgSize.height * zoomLevel}px`,
            backgroundPosition: `-${pos.x * zoomLevel - 75}px -${pos.y * zoomLevel - 75}px`,
            top: pos.y - 75, left: pos.x - 75,
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 40
          }} />
        )}
      </div>
    </div>
  );
};

// 3. Draggable & Resizable Floating Desmos
const FloatingCalculator = ({ geometry, onUpdate, onClose }) => {
  const desmosRef = useRef(null);
  // Track dragging state: 'move', 'resize-e' (east), 'resize-s' (south), 'resize-se' (corner)
  const [dragMode, setDragMode] = useState(null); 
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [startGeo, setStartGeo] = useState(null);

  // Initialize Desmos (Automatically resets on mount because component re-mounts)
  useEffect(() => {
    if (window.Desmos && desmosRef.current && !desmosRef.current.calculator) {
      desmosRef.current.calculator = window.Desmos.GraphingCalculator(desmosRef.current, { keypad:true, expressions:true });
    }
  }, []);

  // Handle Global Mouse Moves for Dragging/Resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!dragMode) return;
      e.preventDefault();

      const dx = e.clientX - startPos.x;
      const dy = e.clientY - startPos.y;
      const next = { ...startGeo };

      if (dragMode === 'move') {
        next.x += dx;
        next.y += dy;
      } else {
        // Resizing Logic
        if (dragMode.includes('e')) next.w = Math.max(300, startGeo.w + dx); // Min width 300
        if (dragMode.includes('s')) next.h = Math.max(200, startGeo.h + dy); // Min height 200
      }

      onUpdate(next);
    };

    const handleMouseUp = () => setDragMode(null);

    if (dragMode) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragMode, startPos, startGeo, onUpdate]);

  const startDrag = (e, mode) => {
    e.stopPropagation();
    setDragMode(mode);
    setStartPos({ x: e.clientX, y: e.clientY });
    setStartGeo(geometry);
  };

  return (
    <div style={{
      ...styles.floatCalc, 
      left: geometry.x, top: geometry.y, 
      width: geometry.w, height: geometry.h 
    }}>
      {/* Header (Move Handle) */}
      <div style={styles.floatHeader} onMouseDown={(e) => startDrag(e, 'move')}>
        <span style={{fontSize:12, fontWeight:700, color:'#333'}}>Desmos Calculator</span>
        <button onClick={onClose} style={{border:'none', background:'transparent', fontSize:16, cursor:'pointer'}}>✕</button>
      </div>

      {/* Calculator Container */}
      <div ref={desmosRef} style={{flex:1, width:'100%', height:'100%', position:'relative'}} />

      {/* --- Resize Handles --- */}
      {/* Right Edge */}
      <div 
        onMouseDown={(e) => startDrag(e, 'resize-e')}
        style={{position:'absolute', top:0, right:0, bottom:10, width:8, cursor:'e-resize', zIndex:10}} 
      />
      {/* Bottom Edge */}
      <div 
        onMouseDown={(e) => startDrag(e, 'resize-s')}
        style={{position:'absolute', bottom:0, left:0, right:10, height:8, cursor:'s-resize', zIndex:10}} 
      />
      {/* Bottom-Right Corner (Diagonal) */}
      <div 
        onMouseDown={(e) => startDrag(e, 'resize-se')}
        style={{
          position:'absolute', bottom:0, right:0, width:16, height:16, 
          cursor:'se-resize', zIndex:20, background: '#cbd5e1',
          borderTopLeftRadius: 4
        }} 
      />
    </div>
  );
};

/**
 * ======================== MAIN COMPONENT ========================
 */
export default function StudentACTTestTaker({ testId, onExit }) {
  const [loading, setLoading] = useState(true);
  const [viewingInstructions, setViewingInstructions] = useState(true);
  const [currentSection, setCurrentSection] = useState(INITIAL_SECTION);
  const [viewingSecurityNotice, setViewingSecurityNotice] = useState(true);
  // Per-section question counts + time limits, resolved from the test itself.
  const [sectionPlan, setSectionPlan] = useState(() => resolveACTSectionPlan());
  const [timeLeft, setTimeLeft] = useState(() => resolveACTSectionPlan()[INITIAL_SECTION].seconds);
  const [extendedTimePercent, setExtendedTimePercent] = useState(0);

  const [agreeIntegrity, setAgreeIntegrity] = useState(false);
  const [recOn, setRecOn] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // --- Integrity Tracking State ---
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [fullscreenExitCount, setFullscreenExitCount] = useState(0);
  const [totalInactiveTime, setTotalInactiveTime] = useState(0);
  
  const blurTimeRef = useRef(null);
  const isInactiveRef = useRef(false);

  // Logic State
  const [questions, setQuestions] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const answersRef = useRef(answers);
  const submittingRef = useRef(false);
  const [bookmarks, setBookmarks] = useState({});
  const [strikeMap, setStrikeMap] = useState({});
  
  // Tools State
  const [showGrid, setShowGrid] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  const [magnifierMode, setMagnifierMode] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1.05);
  const [showTimer, setShowTimer] = useState(true);
  // Store calculator position (x,y) and size (w,h)
  const [calcGeo, setCalcGeo] = useState({ x: 100, y: 100, w: 500, h: 400 });
  const [showScienceChoice, setShowScienceChoice] = useState(false);

  const userId = auth.currentUser?.uid;

  const formatMinutesLabel = (seconds) => {
    const mins = seconds / 60;
    return Number.isInteger(mins) ? `${mins} minutes` : `${mins.toFixed(1)} minutes`;
  };

  const getACTTimeLimit = (sectionKey, percent = extendedTimePercent) =>
    actLimitSeconds(sectionPlan, sectionKey, percent);

  const actTimeLabel = (sectionKey) => {
    const seconds = getACTTimeLimit(sectionKey, extendedTimePercent);
    const isExtended = extendedTimePercent > 0 && sectionKey !== 'BREAK';
    return `${formatMinutesLabel(seconds)}${isExtended ? ' (Extended)' : ''}`;
  };

  // --- 0. Load Desmos API ---
  useEffect(() => {
    if (!document.querySelector('script[data-desmos="act"]')) {
      const s = document.createElement('script');
      s.src = `https://www.desmos.com/api/v1.7/calculator.js?apiKey=${encodeURIComponent(DESMOS_API_KEY)}`;
      s.async = true;
      s.setAttribute('data-desmos', 'act');
      document.body.appendChild(s);
    }
  }, []);

  // --- 1. Resume or Start ---
  useEffect(() => {
    const init = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }
      let extPercent = 0;
      try {
        const accessSnap = await getDoc(doc(db, "student_test_access", `${userId}_${testId}`));
        if (accessSnap.exists()) {
          extPercent = sanitizeExtendedTimePercent(accessSnap.data().extendedTimePercent);
        }
      } catch (err) {
        console.warn("Failed to load extended time setting:", err);
      }
      setExtendedTimePercent(extPercent);

      // Resolve the section plan from the questions actually stored with the
      // test. Classic (75/60/40/40) and enhanced (50/45/36/40) forms are paced
      // differently, so the clock must not be hard-coded.
      let plan = resolveACTSectionPlan();
      try {
        const counts = {};
        await Promise.all(
          ACT_SECTION_ORDER.map(async (section) => {
            const countSnap = await getCountFromServer(
              query(
                collection(db, "act_diagnostic_tests", testId, "questions"),
                where("subject", "==", section)
              )
            );
            counts[section] = countSnap.data().count;
          })
        );
        let overrides = {};
        const testSnap = await getDoc(doc(db, "act_diagnostic_tests", testId));
        if (testSnap.exists() && testSnap.data().timing) {
          overrides = testSnap.data().timing;
        }
        plan = resolveACTSectionPlan({ counts, overrides });
      } catch (err) {
        console.warn("Failed to load ACT section timing, using defaults:", err);
      }
      setSectionPlan(plan);

      const snap = await getDoc(doc(db, "diagnostic_results", userId, "state", "act_state"));
      if (snap.exists() && snap.data().testId === testId) {
        const d = snap.data();
        const resumeSection = d.currentSection || INITIAL_SECTION;
        const limit = actLimitSeconds(plan, resumeSection, extPercent);
        setCurrentSection(resumeSection);
        setTimeLeft(
          typeof d.timeLeft === 'number' && d.timeLeft > 0
            ? (limit > 0 ? Math.min(d.timeLeft, limit) : d.timeLeft)
            : limit
        );
        setAnswers(d.answers || {});
        setBookmarks(d.bookmarks || {});
        setStrikeMap(d.strikeMap || {});
        setQIdx(d.qIdx || 0); // Resume exact question
        setViewingInstructions(false); // Assume if resuming, they passed instructions
        setViewingSecurityNotice(false); // Skip security notice if resuming
        setAgreeIntegrity(false);
        if (d.integrityLog) {
          setTabSwitchCount(d.integrityLog.tabSwitchCount || 0);
          setFullscreenExitCount(d.integrityLog.fullscreenExitCount || 0);
          setTotalInactiveTime(d.integrityLog.totalInactiveTime || 0);
        }
      } else {
        setTimeLeft(actLimitSeconds(plan, INITIAL_SECTION, extPercent));
      }
      setLoading(false);
    };
    init();
  }, [userId, testId]);

  // Sync answersRef with answers state to fix stale closure in timer
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  // --- 2. Load Questions ---
  useEffect(() => {
    if (currentSection === 'BREAK' || currentSection === 'FINISH') {
      setQuestions([]); return;
    }
    const loadQ = async () => {
      const q = query(
        collection(db, "act_diagnostic_tests", testId, "questions"),
        where("subject", "==", currentSection),
        orderBy("questionNumber") 
      );
      const snap = await getDocs(q);
      const list = snap.docs.map(d => d.data());
      setQuestions(list);
    };
    loadQ();
  }, [currentSection, testId]);

  // --- 3. Timer ---
  useEffect(() => {
    // ADDED: "|| showScienceChoice" to the check below
    if (loading || viewingInstructions || currentSection === 'FINISH' || showScienceChoice) return;
    
    const t = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { handleSectionComplete(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, viewingInstructions, currentSection, showScienceChoice]);

  // --- 4. Auto-Save (Immediate on Interaction) ---
  const saveState = async () => {
    if (!userId) return;
    await setDoc(doc(db, "diagnostic_results", userId, "state", "act_state"), {
      testId, currentSection, timeLeft, answers, bookmarks, strikeMap, qIdx,
      integrityLog: {
        tabSwitchCount,
        fullscreenExitCount,
        totalInactiveTime
      },
      updatedAt: serverTimestamp()
    });
    // Additive: publish a flat, queryable summary for the Admin HomeScreen.
    saveDiagnosticLiveProgress({
      testType: 'ACT',
      testId,
      studentId: userId,
      studentName: auth.currentUser?.displayName || '',
      stageKey: currentSection,
      answeredCount: questions.reduce((acc, q) => (answers[q.questionId] ? acc + 1 : acc), 0),
      totalQuestions: questions.length,
      sectionTimeLeftSeconds: timeLeft,
    });
  };

  useEffect(() => {
    if (!loading) saveState();
    // eslint-disable-next-line
  }, [answers, qIdx, currentSection]); 

  // --- 5. Keyboard Navigation ---
  useEffect(() => {
    const handleKey = (e) => {
      if (viewingInstructions || currentSection === 'BREAK') return;
      if (e.target.tagName === 'INPUT') return; // Don't block typing
      
      if (e.key === 'ArrowRight') {
         if (qIdx < questions.length - 1) setQIdx(i => i + 1);
      } else if (e.key === 'ArrowLeft') {
         if (qIdx > 0) setQIdx(i => i - 1);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [qIdx, questions.length, viewingInstructions, currentSection]);

  useEffect(() => {
    const id = setInterval(() => setRecOn(prev => !prev), 1200); // 1.2s blink
    return () => clearInterval(id);
  }, []);

  // ---- Integrity Tracking Hook ----
  useEffect(() => {
    if (loading || viewingInstructions || currentSection === 'FINISH' || showScienceChoice) return;

    const handleInactivate = () => {
      if (isInactiveRef.current) return;
      isInactiveRef.current = true;
      blurTimeRef.current = Date.now();
    };

    const handleActivate = () => {
      if (!isInactiveRef.current) return;
      isInactiveRef.current = false;
      if (blurTimeRef.current) {
        const awayTimeSec = Math.floor((Date.now() - blurTimeRef.current) / 1000);
        setTotalInactiveTime(prev => prev + awayTimeSec);
        
        if (awayTimeSec >= 2) {
          setTabSwitchCount(prev => prev + 1);
        }
        
        blurTimeRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) handleInactivate();
      else handleActivate();
    };

    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setFullscreenExitCount(prev => prev + 1);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleInactivate);
    window.addEventListener("focus", handleActivate);
    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleInactivate);
      window.removeEventListener("focus", handleActivate);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [loading, viewingInstructions, currentSection, showScienceChoice]);

  // --- LOGIC: Navigation & Science Optional ---
  const handleSectionComplete = async () => {
    // 1. Prepare answers for THIS section only (fill blanks with "")
    const sectionAnswers = {};
    questions.forEach(q => {
      // NEW (Fixed):
      sectionAnswers[q.questionId] = answersRef.current[q.questionId] || "";
    });

    // 2. Save filtered response
    const responseId = `${userId}_${testId}_${currentSection}`;
    await setDoc(doc(db, "student_responses", responseId), {
      studentId: userId, 
      testId, 
      section: currentSection, 
      answers: sectionAnswers, // <--- Now contains only this section's data
      completedAt: serverTimestamp()
    });

    // Flow Logic
    if (currentSection === 'English') {
      setCurrentSection('Math');
      setTimeLeft(getACTTimeLimit('Math', extendedTimePercent));
      setViewingInstructions(true);
    } 
    else if (currentSection === 'Math') {
      setCurrentSection('BREAK');
      setTimeLeft(getACTTimeLimit('BREAK', extendedTimePercent));
    } 
    else if (currentSection === 'BREAK') {
      setCurrentSection('Reading');
      setTimeLeft(getACTTimeLimit('Reading', extendedTimePercent));
      setViewingInstructions(true);
    } 
    else if (currentSection === 'Reading') {
      // Instead of window.confirm, we show the custom interstitial page
      setShowScienceChoice(true);
      return; // Stop here, wait for user input on the new screen
    }
    else if (currentSection === 'Science') {
      // Ensure we only call this once
      if (!loading) {
        await finishTest(false);
      }
      return; // Add return to stop any further logic in this function
    }

    setQIdx(0);
    setCalcOpen(false);
    window.scrollTo(0,0);
  };

  const finishTest = async (skippedScience) => {
    // 1. Check the Instant Lock
    if (submittingRef.current) return;
    
    // 2. Set the Instant Lock
    submittingRef.current = true;
    setLoading(true);
    const allQSnap = await getDocs(collection(db, "act_diagnostic_tests", testId, "questions"));
    const allQs = allQSnap.docs.map(d => d.data());

    // 0. Ensure ALL questions have an entry (fill blanks with "")
    const finalAnswers = { ...answersRef.current };
    allQs.forEach(q => {
      if (finalAnswers[q.questionId] === undefined) {
        finalAnswers[q.questionId] = "";
      }
    });

    // 1. Calculate RAW Scores
    let rawScores = { English: 0, Math: 0, Reading: 0, Science: 0 };
    
    allQs.forEach(q => {
      if (q.category === "Not Scored") return; 
      // CHANGE: Use finalAnswers here
      if (finalAnswers[q.questionId] === q.correctAnswer) {
        if (rawScores[q.subject] !== undefined) rawScores[q.subject]++;
      }
    });

    // 2. Fetch SCALE Scores from DB
    // We look up /act_diagnostic_tests/{testId}/{Subject}_scores/{rawScore}
    let scaleScores = { English: 0, Math: 0, Reading: 0, Science: 0 };
    const subjects = ['English', 'Math', 'Reading', 'Science'];

    for (const sub of subjects) {
      if (sub === 'Science' && skippedScience) {
        scaleScores.Science = 0;
        continue;
      }
      
      const raw = rawScores[sub];
      try {
        // Doc ID is the raw score string
        const scaleRef = doc(db, "act_diagnostic_tests", testId, `${sub}_scores`, String(raw));
        const scaleSnap = await getDoc(scaleRef);
        
        if (scaleSnap.exists()) {
          scaleScores[sub] = scaleSnap.data().scale;
        } else {
          console.warn(`No scale found for ${sub} raw ${raw}`);
          scaleScores[sub] = 0; // Default to 0 if missing
        }
      } catch (err) {
        console.error(`Error fetching scale for ${sub}:`, err);
      }
    }

    // 3. Calculate STEM (Average of Math & Science SCALE scores)
    let stem = 0;
    if (skippedScience) {
      stem = scaleScores.Math; // Fallback if science skipped
    } else {
      stem = Math.round((scaleScores.Math + scaleScores.Science) / 2);
    }

    // --- NEW: Calculate Composite Score ---
    // Average of English, Math, Reading, and Science (if taken)
    let compositeSum = scaleScores.English + scaleScores.Math + scaleScores.Reading;
    let divisor = 3;

    if (!skippedScience) {
      compositeSum += scaleScores.Science;
      divisor = 4;
    }

    // Round to nearest whole number
    const composite = Math.round(compositeSum / divisor);

    const resultId = `${userId}_${testId}`;
    
    // 4. Save Results
    await setDoc(doc(db, "diagnostic_results", resultId), {
      studentId: userId, 
      testId, 
      type: 'ACT', 
      status: 'completed',
      completedAt: serverTimestamp(),
      rawScores: rawScores,      // Save Raw
      scaleScores: scaleScores,  // Save Scaled (NEW)
      stemScore: stem,           // Saved Stem (Calculated from Scale)
      compositeScore: composite,
      scienceSkipped: !!skippedScience,
      answers: finalAnswers,
      integrityLog: {
        tabSwitchCount,
        fullscreenExitCount,
        totalInactiveTime,
        cheated_triggered: (tabSwitchCount > 2 || totalInactiveTime > 30 || fullscreenExitCount > 2)
      }
    });

    await setDoc(doc(db, "student_test_access", resultId), { finished: true }, { merge: true });
    await deleteDoc(doc(db, "diagnostic_results", userId, "state", "act_state"));

    // Mark the live-progress summary completed for the Admin HomeScreen.
    markDiagnosticLiveProgressComplete({
      testType: 'ACT',
      testId,
      studentId: userId,
      attemptId: resultId,
      scoreSummary: { compositeScore: composite, scaleScores, scienceSkipped: !!skippedScience },
    });

    alert("Test Submitted!");
    onExit();
  };

  const getOptions = (qNum) => (qNum % 2 !== 0) ? ['A', 'B', 'C', 'D'] : ['F', 'G', 'H', 'J'];
  
  // New Time Formatter for HH:MM:SS
  const formatTimeFull = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // Calculate Answered Count
  const answeredCount = questions.reduce((acc, q) => {
    return answers[q.questionId] ? acc + 1 : acc;
  }, 0);

  // --- RENDER ---
  if (loading) return <div style={{padding:50, textAlign:'center'}}>Loading...</div>;

  // 0. SECURITY NOTICE SCREEN (Runs once at the very beginning)
  if (viewingSecurityNotice) {
    const handleAgreeAndContinue = () => {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch((e) => console.log("Fullscreen blocked:", e));
      }
      setViewingSecurityNotice(false);
    };

    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={styles.logoRow}>
              <img src="/images/logo.png" alt="Logo" style={{ height: 20, width: 'auto' }} />
              <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '0.3px', color: '#fff' }}>True Education</span>
              <div style={{ height: 16, width: 1, background: '#64748b', margin: '0 14px' }}></div>
              <span style={{ fontSize: 15, fontWeight: 400, color: '#e2e8f0' }}>Security Notice</span>
            </div>
          </div>
        </div>

        <div style={styles.instructionPage}>
          <div style={styles.instructionTitle}>ACT Diagnostic Test Security</div>
          
          <div style={styles.warningBox}>
            <div style={{fontSize: 24}}>🚨</div>
            <div>
              <strong>OFFICIAL TEST SECURITY NOTICE</strong><br/>
              This ACT diagnostic session is actively monitored in real time.<br/>
              <br/>
              <strong>DO NOT</strong> switch tabs, minimize the window, open other applications, or exit full-screen mode.<br/>
              These actions are automatically detected and permanently logged to your test record.<br/>
              <br/>
              <strong>Repeated violations will trigger immediate submission</strong> and an <strong>Integrity Flag</strong> on your score report.<br/>
              Start only if you are ready to test without interruption.<br/>
            </div>
          </div>

          <label style={{ display: 'flex', gap: 10, marginTop: 14, fontSize: 14, color: '#334155', alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={agreeIntegrity}
              onChange={(e) => setAgreeIntegrity(e.target.checked)}
              style={{ marginTop: 4 }}
            />
            <span>
              I understand that this session is monitored and that leaving the test page or exiting full-screen may result in automatic submission and a flagged score report.
            </span>
          </label>

          <div style={{ display: 'flex', gap: 12, marginTop: 40 }}>
            <button
              type="button"
              style={{
                flex: 1,
                padding: '14px 32px',
                fontSize: 16,
                fontWeight: 700,
                background: '#ffffff',
                color: '#0f172a',
                border: '1px solid #cbd5e1',
                borderRadius: 8,
                cursor: 'pointer'
              }}
              onClick={onExit}
            >
              Cancel / Go Back
            </button>

            <button
              style={{
                ...styles.startSectionBtn,
                flex: 2,
                marginTop: 0,
                opacity: agreeIntegrity ? 1 : 0.6,
                cursor: agreeIntegrity ? 'pointer' : 'not-allowed'
              }}
              onClick={handleAgreeAndContinue}
              disabled={!agreeIntegrity}
            >
              Agree and Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 1. INSTRUCTIONS SCREEN
  if (viewingInstructions) {
    const handleStartTest = async () => {
      // Guard: Prevent starting if questions haven't loaded yet
      if (questions.length === 0) {
        alert("Please wait a moment while your test questions load.");
        return;
      }
      
      // Store initial student_responses right when they actually start
      const responseId = `${auth.currentUser?.uid}_${testId}_${currentSection}`;
      const sectionAnswers = {};
      for (const q of questions) sectionAnswers[q.questionId] = answers[q.questionId] ?? "";

      try {
        await setDoc(doc(db, "student_responses", responseId), {
          studentId: auth.currentUser?.uid,
          testId,
          section: currentSection,
          answers: sectionAnswers,
          startedAt: serverTimestamp() // Mark exactly when they started
        }, { merge: true });
      } catch (err) {
        console.error("Failed to initialize student_responses:", err);
      }

      setViewingInstructions(false);
    };

    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={styles.logoRow}>
              <img src="/images/logo.png" alt="Logo" style={{ height: 20, width: 'auto' }} />
              <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '0.3px', color: '#fff' }}>True Education</span>
              <div style={{ height: 16, width: 1, background: '#64748b', margin: '0 14px' }}></div>
              <span style={{ fontSize: 15, fontWeight: 400, color: '#e2e8f0' }}>ACT {currentSection}</span>
            </div>
          </div>
        </div>

        <div style={styles.instructionPage}>
          <div style={styles.instructionTitle}>{currentSection} Test Instructions</div>
          <div 
            style={{...styles.instructionText, whiteSpace: 'normal'}} 
            dangerouslySetInnerHTML={{
              __html: (DIRECTIONS[currentSection] || "Please follow proctor instructions.")
                .replace('__TIME_LABEL__', actTimeLabel(currentSection))
                .replace('__Q_COUNT__', String(sectionPlan[currentSection]?.count ?? questions.length))
            }}
          />
          
          <div style={{ display: 'flex', gap: 12, marginTop: 40 }}>
            <button
              style={{
                ...styles.startSectionBtn,
                marginTop: 0,
                width: '100%',
                opacity: questions.length > 0 ? 1 : 0.6,
                cursor: questions.length > 0 ? 'pointer' : 'not-allowed'
              }}
              onClick={handleStartTest}
              disabled={questions.length === 0}
            >
              {questions.length === 0 ? 'Loading Questions...' : `Start ${currentSection} Test`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 2. BREAK SCREEN
  if (currentSection === 'BREAK') {
    return (
      <div style={{...styles.page, justifyContent:'center', alignItems:'center'}}>
        <h1>10 Minute Break</h1>
        <h2 style={{fontSize:48, margin:'20px 0'}}>{formatTimeFull(timeLeft)}</h2>
        <button style={styles.startSectionBtn} onClick={handleSectionComplete}>Skip Break</button>
      </div>
    );
  }

  // 3. OPTIONAL SCIENCE INTERSTITIAL SCREEN
  if (showScienceChoice) {
    return (
      <div style={{...styles.page, justifyContent:'center', alignItems:'center', padding: 20}}>
        <div style={{maxWidth: 600, textAlign: 'center', background: '#fff', padding: 40, borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.1)'}}>
          <h2 style={{fontSize: 28, fontWeight: 800, color: '#0f172a', marginBottom: 16}}>Reading Section Complete</h2>
          <p style={{fontSize: 16, color: '#475569', marginBottom: 32, lineHeight: 1.6}}>
            You have finished the Reading section.<br/>The Science section is OPTIONAL on the ACT.<br/>
            Would you like to continue to Science or submit your ACT exam now?
          </p>
          
          <div style={{display: 'flex', flexDirection: 'column', gap: 16}}>
            {/* Option A: Take Science */}
            <button 
              style={{...styles.startSectionBtn, marginTop: 0, width: '100%'}} 
              onClick={() => {
                setShowScienceChoice(false);
                setCurrentSection('Science');
                setTimeLeft(getACTTimeLimit('Science', extendedTimePercent));
                setViewingInstructions(true);
                setQIdx(0);
              }}
            >
              Proceed to Science Section
            </button>
            
            {/* Option B: Finish Test */}
            <button 
              style={{
                ...styles.startSectionBtn, 
                marginTop: 0, width: '100%', 
                background: '#fff', color: '#64748b', 
                border: '2px solid #e2e8f0'
              }} 
              onClick={() => finishTest(true)}
            >
              Skip Science & Finish Test
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentQ = questions[qIdx];
  const isMath = currentSection === 'Math';
  const passagePath = (!isMath && currentQ?.passageImagePath) ? currentQ.passageImagePath : null;

  return (
    <div style={styles.page}>
      
      {/* HEADER */}
      <div style={styles.header}>
        <div style={{display:'flex', alignItems:'center'}}>
          {/* Logo & Title Restored - Fixed Size */}
          <div style={styles.logoRow}>
            <img src="/images/logo.png" alt="Logo" style={{height: 20, width: 'auto'}} />
            <span style={{fontSize: 15, fontWeight: 700, letterSpacing: '0.3px'}}>True Education</span>
            <div style={{height: 16, width: 1, background: '#64748b', margin: '0 14px'}}></div>
            <span style={{fontSize: 15, fontWeight: 400, color: '#e2e8f0'}}>ACT {currentSection}</span>
          </div>

          {/* Tools */}
          <div style={styles.headerTools}>
            {/* Renamed to View All */}
            <button 
              style={{...styles.headerBtn, ...(showGrid ? styles.headerBtnActive : {})}} 
              onClick={() => setShowGrid(!showGrid)}
            >
              <LuLayoutGrid size={16} /> View All
            </button>

            <button 
               style={{...styles.headerBtn, ...(bookmarks[currentQ?.questionId] ? styles.headerBtnActive : {})}} 
               onClick={() => {
                 const id = currentQ.questionId;
                 setBookmarks(p => ({...p, [id]: !p[id]}));
               }}
            >
              <LuBookmark size={16} /> BOOKMARK
            </button>

            {isMath && (
              <button 
                style={{...styles.headerBtn, ...(calcOpen ? styles.headerBtnActive : {})}} 
                onClick={() => setCalcOpen(!calcOpen)}
              >
                <LuCalculator size={16} /> CALCULATOR
              </button>
            )}
            
            {!isMath && (
               <button 
                 style={{...styles.headerBtn, ...(magnifierMode ? styles.headerBtnActive : {})}} 
                 onClick={() => setMagnifierMode(!magnifierMode)}
               >
                 <LuZoomIn size={16} /> MAGNIFIER
               </button>
            )}
          </div>
        </div>

        {/* Right Side: Stats & Timer */}
        <div style={styles.headerStats}>
          
          {/* Fake Recording Indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#f01523', fontSize: 13, fontWeight: 800, paddingRight: 16, borderRight: '1px solid #334155' }}>
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: '#f01523',
                opacity: recOn ? 1 : 0.25,
                boxShadow: recOn ? '0 0 10px #f01523' : '0 0 0px transparent',
                transition: 'opacity 800ms ease, box-shadow 800ms ease'
              }}
            ></div>
            REC
          </div>

          <div style={styles.statItem}>
            <span style={{color: '#94a3b8'}}>ANSWERED</span>
            <span>{answeredCount} of {questions.length}</span>
          </div>
          
          <div style={styles.statItem}>
            <span style={{color: '#94a3b8'}}>TOTAL TIME LEFT</span>
            <span style={{fontVariantNumeric: 'tabular-nums', visibility: showTimer ? 'visible' : 'hidden'}}>
              {formatTimeFull(timeLeft)}
            </span>
            <button 
              onClick={() => setShowTimer(!showTimer)}
              style={{background:'none', border:'none', color:'#fff', cursor:'pointer', padding:4, display:'flex'}}
            >
              {showTimer ? <FaEye size={18} /> : <FaEyeSlash size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* SPLIT LAYOUT */}
      <div style={styles.splitWrap}>
        
        {/* LEFT PANE: PASSAGE */}
        {!isMath && (
          <div style={styles.leftPane}>
             {passagePath ? (
               magnifierMode ? (
                 <MagnifierPane src={passagePath} zoomLevel={zoomLevel} setZoomLevel={setZoomLevel} />
               ) : (
                 <div style={{padding:24, overflowY:'auto', height:'100%', boxSizing: 'border-box'}}>
                    <SafeImage path={passagePath} style={{width:'100%'}} />
                </div>
               )
             ) : (
               <div style={{color:'#94a3b8', padding:24}}>No passage for this item.</div>
             )}
          </div>
        )}

        {/* RIGHT PANE: QUESTION & ANSWERS (Split Vertically) */}
        <div style={styles.rightPane(isMath)}>
          
          {/* Top: Question */}
          <div style={styles.rightTop}>
            {currentQ ? (
              <>
                <div style={{fontSize:12, fontWeight:700, color:'#64748b', marginBottom:12}}>
                  Question {qIdx + 1} of {questions.length}
                </div>
                <SafeImage 
                    path={currentQ.questionImagePath} 
                    style={styles.qImg(isMath)} 
                />
              </>
            ) : <div>Loading...</div>}
          </div>

          {/* Bottom: Answers */}
          <div style={styles.rightBottom}>
            <div style={{fontSize:12, fontWeight:700, marginBottom:16, color:'#334155'}}>
              SELECT ANSWER
            </div>
            {currentQ && getOptions(currentQ.questionNumber).map(opt => {
              const qId = currentQ.questionId;
              const selected = answers[qId] === opt;
              const struck = strikeMap[qId]?.[opt];

              return (
                <div 
                  key={opt} 
                  style={styles.optionRow(selected, struck)}
                  onClick={() => setAnswers(p => ({...p, [qId]: opt}))}
                >
                  <div style={styles.optionLabel(selected)}>{opt}</div>
                  <div style={{flex:1, fontWeight:600}}>
                    Option {opt}
                  </div>
                  <button 
                    style={styles.strikeBtn(struck)}
                    onClick={(e) => {
                      e.stopPropagation();
                      setStrikeMap(p => ({
                        ...p, 
                        [qId]: { ...(p[qId]||{}), [opt]: !struck }
                      }));
                    }}
                  >
                    {struck ? 'Un-Eliminate' : 'Eliminate'}
                  </button>
                </div>
              );
            })}
          </div>

        </div>
      </div>

      {/* FOOTER */}
      <div style={styles.footer}>
        <div style={{color: '#334155', fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4}}>
          Use 
          <kbd style={{fontFamily:'inherit', border:'1px solid #ccc', padding:'2px 4px', borderRadius:4, display:'inline-flex', alignItems:'center', background:'#fff'}}>
            <LuArrowLeft size={12} />
          </kbd> 
          <kbd style={{fontFamily:'inherit', border:'1px solid #ccc', padding:'2px 4px', borderRadius:4, display:'inline-flex', alignItems:'center', background:'#fff'}}>
            <LuArrowRight size={12} />
          </kbd> 
          for quick nav
        </div>
        <div style={{display:'flex', gap:12}}>
           {/* Previous Button (Moved to Right) */}
           <button 
             style={styles.navBtn(false)}
             disabled={qIdx === 0}
             onClick={() => setQIdx(i => i - 1)}
           >
             Previous
           </button>
           
           <button 
             style={styles.navBtn(true)}
             onClick={() => {
               if (qIdx < questions.length - 1) {
                 setQIdx(i => i + 1);
               } else {
                 setShowConfirmModal(true); // Open custom modal instead of native confirm
               }
             }}
           >
             {qIdx === questions.length - 1 ? 'Submit' : 'Next'}
           </button>
        </div>
      </div>

      {/* TOOLS */}
      {calcOpen && isMath && (
        <FloatingCalculator 
            geometry={calcGeo} 
            onUpdate={setCalcGeo} 
            onClose={() => setCalcOpen(false)} 
        />
      )}

      {/* Custom Confirm Modal */}
      {showConfirmModal && (
        <div style={styles.overlay}>
          <div style={{...styles.gridModal, width: 400, padding: '32px 24px', textAlign: 'center', margin: 'auto'}}>
            <h3 style={{marginTop: 0, fontSize: 20, color: '#0f172a'}}>Submit Section?</h3>
            <p style={{color: '#475569', marginBottom: 24, lineHeight: 1.5}}>
              Are you sure you want to submit this section and move on? You will not be able to return to these questions.
            </p>
            <div style={{display: 'flex', gap: 12, justifyContent: 'center'}}>
              <button 
                style={{...styles.navBtn(false), flex: 1}} 
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </button>
              <button 
                style={{...styles.navBtn(true), flex: 1}} 
                onClick={() => {
                  setShowConfirmModal(false);
                  handleSectionComplete();
                }}
              >
                Yes, Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grid Modal */}
      {showGrid && (
        <div style={styles.overlay} {...backdropDismiss(() => setShowGrid(false))}>
          <div style={styles.gridModal} onClick={e => e.stopPropagation()}>
            <div style={{padding:20, fontWeight:700, borderBottom:'1px solid #eee'}}>
              Review Section: {currentSection}
            </div>
            <div style={{padding:20, overflowY:'auto', display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(50px, 1fr))', gap:10}}>
              {questions.map((q, idx) => {
                const ans = answers[q.questionId];
                const mark = bookmarks[q.questionId];
                return (
                  <button 
                    key={q.questionId}
                    onClick={() => { setQIdx(idx); setShowGrid(false); }}
                    style={{
                      height: 40, borderRadius: 6, border: '1px solid #ddd',
                      background: idx === qIdx ? '#2563eb' : (ans ? '#dbeafe' : '#fff'),
                      color: idx === qIdx ? '#fff' : '#000',
                      position: 'relative'
                    }}
                  >
                    {idx + 1}
                    {mark && <span style={{position:'absolute', top:-4, right:-4, color:'orange', fontSize:16}}>★</span>}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}